import { Link } from "react-router-dom";
import { useState } from "react";
import { Code, ArrowUpRight, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ENDPOINTS = [
  {
    method: "GET",
    path: "/api/v1/public/health",
    desc: "Service heartbeat. No params.",
    sample: `{ "ok": true, "service": "Symposia Public API", "version": "v1" }`,
  },
  {
    method: "GET",
    path: "/api/v1/public/conferences",
    desc: "Paginated list of all conferences with optional filters.",
    params: [
      ["q", "string", "Free-text search (title + description)"],
      ["topic", "string", "Exact topic match (e.g. \"Artificial Intelligence\")"],
      ["type", "string", "academic | tech | both"],
      ["country", "string", "ISO-3166 alpha-2 (e.g. US, GB, JP)"],
      ["page", "int", "Page number (default 1)"],
      ["limit", "int", "Items per page (1–100, default 25)"],
    ],
    sample: `{
  "total": 152,
  "page": 1,
  "limit": 25,
  "pages": 7,
  "items": [
    {
      "id": "uuid",
      "title": "NeurIPS 2026",
      "topic": "Artificial Intelligence",
      "type": "academic",
      "location": { "city": "Vancouver", "country": "CA", ... },
      "start_date": "2026-12-08",
      "end_date": "2026-12-14",
      "website": "https://neurips.cc",
      "issn": ["1049-5258"],
      "proceedings": { "publisher": "...", "indexed_papers_count": 12 }
    }
  ]
}`,
  },
  {
    method: "GET",
    path: "/api/v1/public/conferences/{id}",
    desc: "Full details for one conference including linked speakers and papers.",
    sample: `{
  "id": "uuid",
  "title": "...",
  "speakers": [{ "id": "...", "name": "...", "organization": "..." }],
  "papers": [{ "id": "...", "title": "...", "abstract": "...", "authors": [...] }]
}`,
  },
  {
    method: "GET",
    path: "/api/v1/public/topics",
    desc: "List of all topics with conference counts, sorted by frequency.",
    sample: `[ { "topic": "Artificial Intelligence", "count": 14 }, ... ]`,
  },
  {
    method: "GET",
    path: "/api/v1/public/countries",
    desc: "List of all countries with conference counts.",
    sample: `[ { "country": "US", "count": 22 }, ... ]`,
  },
];

export default function DevelopersPage() {
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid="developers-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-5xl">
      <div className="overline mb-3">Developers · Public API</div>
      <h1 className="font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[.92] mb-6">
        The Symposia <span className="italic font-medium text-blue-700">Public API</span>
      </h1>
      <p className="text-zinc-600 text-lg leading-relaxed max-w-3xl">
        A free, read-only HTTP API over our curated catalog of academic and industry conferences.
        No API key required. Returns clean, predictable JSON.
      </p>

      <div className="mt-10 grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <FactCell h="Base URL" v={`${base}/api/v1/public`} mono />
        <FactCell h="Rate limit" v="60 req · 60s · per IP" />
        <FactCell h="Auth" v="None — public read-only" />
      </div>

      <section className="mt-14">
        <div className="overline mb-3">Quick start</div>
        <h2 className="font-display font-bold text-3xl tracking-tight mb-5">Curl it</h2>
        <CodeBlock code={`curl '${base}/api/v1/public/conferences?topic=Artificial%20Intelligence&limit=5'`} />
      </section>

      <section className="mt-14">
        <div className="overline mb-3">Reference</div>
        <h2 className="font-display font-bold text-3xl tracking-tight mb-5">Endpoints</h2>
        <div className="space-y-10">
          {ENDPOINTS.map((ep) => (
            <Endpoint key={ep.path} ep={ep} base={base} />
          ))}
        </div>
      </section>

      <section className="mt-16 border-t border-zinc-950 pt-6">
        <div className="overline mb-3">Notes</div>
        <ul className="space-y-2 text-zinc-700 list-disc ps-5 max-w-3xl">
          <li>Catalog data is aggregated from public sources (conference sites, OpenAlex) and curated by our editors. Always verify on the official site before acting on dates or deadlines.</li>
          <li>Returns <code className="font-mono text-sm bg-zinc-100 px-1.5">429</code> when you exceed the rate limit. Back off and retry.</li>
          <li>This API is versioned at <code className="font-mono text-sm bg-zinc-100 px-1.5">/api/v1/public/*</code>. Breaking changes will only ship in a new major version.</li>
          <li>Questions or want write access? <a className="underline" href="mailto:hello@conferencely.org">hello@conferencely.org</a></li>
        </ul>
      </section>

      <section className="mt-12 flex flex-wrap gap-3">
        <Link to="/about" data-testid="dev-about-link" className="btn-secondary">About Symposia</Link>
        <Link to="/conferences" data-testid="dev-browse-link" className="btn-primary">Browse the index <ArrowUpRight size={14} data-arrow="forward" /></Link>
      </section>
    </div>
  );
}

function FactCell({ h, v, mono }) {
  return (
    <div className="bg-white p-5">
      <div className="overline">{h}</div>
      <div className={(mono ? "font-mono text-sm" : "font-display font-medium text-base") + " mt-1 break-all"}>{v}</div>
    </div>
  );
}

function Endpoint({ ep, base }) {
  const tryUrl = `${base}${ep.path.replace("{id}", "")}`.replace(/\/$/, "");
  return (
    <div className="border-2 border-black bg-white" data-testid={`endpoint-${ep.path}`}>
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs font-bold text-white bg-blue-600 px-2 py-1">{ep.method}</span>
          <code className="font-mono text-sm md:text-base font-semibold">{ep.path}</code>
        </div>
        {!ep.path.includes("{") && (
          <a href={tryUrl} target="_blank" rel="noreferrer" className="text-xs link-ul font-medium inline-flex items-center gap-1">
            Try it <ArrowUpRight size={12} data-arrow="forward" />
          </a>
        )}
      </div>
      <div className="p-5">
        <p className="text-sm text-zinc-700 mb-4">{ep.desc}</p>
        {ep.params && (
          <div className="mb-4">
            <div className="overline mb-2">Query params</div>
            <table className="w-full text-xs font-mono">
              <thead className="text-zinc-400">
                <tr className="border-b border-zinc-200"><th className="text-start py-1 pe-4">name</th><th className="text-start py-1 pe-4">type</th><th className="text-start py-1">description</th></tr>
              </thead>
              <tbody>
                {ep.params.map((p) => (
                  <tr key={p[0]} className="border-b border-zinc-100">
                    <td className="py-1.5 pe-4 text-zinc-950 font-semibold">{p[0]}</td>
                    <td className="py-1.5 pe-4 text-blue-700">{p[1]}</td>
                    <td className="py-1.5 text-zinc-600">{p[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <div className="overline mb-2">Response (excerpt)</div>
          <CodeBlock code={ep.sample} />
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(code); setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Couldn't copy"); }
  }
  return (
    <div className="relative">
      <pre className="bg-zinc-950 text-zinc-100 text-xs leading-relaxed p-4 overflow-x-auto font-mono whitespace-pre-wrap break-words">{code}</pre>
      <button onClick={copy} data-testid="code-copy" className="absolute top-2 right-2 rtl:right-auto rtl:left-2 p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300" aria-label="Copy">
        {copied ? <CheckCircle size={14} weight="fill" className="text-blue-400" /> : <Copy size={14} />}
      </button>
    </div>
  );
}
